/**
 * Wake Lock utility to prevent device sleep during long operations
 * Uses the Wake Lock API with fallback strategies for older browsers
 */

import { mobileWakeLock } from './mobileWakeLock';

// Debug flag - set to true to enable wake lock logging
const DEBUG_WAKELOCK = false;

class WakeLockManager {
  private wakeLock: WakeLockSentinel | null = null;
  private noSleepVideo: HTMLVideoElement | null = null;
  private isActive: boolean = false;
  private reacquireInterval: NodeJS.Timeout | null = null;
  private lastAcquireTime: number = 0;

  /**
   * Request a wake lock to keep the screen on
   * @param reason Optional reason for debugging
   */
  async requestWakeLock(reason?: string): Promise<boolean> {
    // TEMPORARY: Disable wake lock in development to prevent infinite loops
    if (process.env.NODE_ENV === 'development') {
      if (DEBUG_WAKELOCK) console.log(`[WakeLock] Skipped in development${reason ? `: ${reason}` : ''}`);
      return true;
    }

    if (DEBUG_WAKELOCK) console.log(`[WakeLock] Requesting wake lock${reason ? `: ${reason}` : ''}`);
    this.lastAcquireTime = Date.now();

    // Release any existing wake lock first
    if (this.wakeLock || this.noSleepVideo) {
      await this.releaseWakeLock();
    }

    // Try native Wake Lock API first (Chrome 84+, Edge 84+, Safari 16.4+)
    if ('wakeLock' in navigator && navigator.wakeLock) {
      try {
        this.wakeLock = await (navigator as any).wakeLock.request('screen');
        this.isActive = true;
        
        // Handle wake lock release on visibility change
        this.wakeLock.addEventListener('release', () => {
          if (DEBUG_WAKELOCK) console.log('[WakeLock] Wake lock was released by system');
          this.isActive = false;
          // Try to reacquire immediately
          if (document.visibilityState === 'visible') {
            setTimeout(() => this.requestWakeLock('auto-reacquire'), 100);
          }
        });

        if (DEBUG_WAKELOCK) console.log('[WakeLock] Wake lock acquired successfully using native API');
        
        // Also start mobile-specific strategies
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
          mobileWakeLock.start();
        }
        
        this.startReacquireInterval();
        return true;
      } catch (err: any) {
        if (DEBUG_WAKELOCK) console.warn('[WakeLock] Failed to acquire native wake lock:', err?.message || err);
        // Continue to fallback methods
      }
    } else {
      if (DEBUG_WAKELOCK) console.log('[WakeLock] Native Wake Lock API not available');
    }

    // Fallback 1: Use NoSleep.js approach with video
    try {
      const success = await this.enableVideoWakeLock();
      if (success) {
        // Also start mobile-specific strategies
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
          mobileWakeLock.start();
        }
        this.startReacquireInterval();
        return true;
      }
    } catch (err) {
      if (DEBUG_WAKELOCK) console.warn('[WakeLock] Video wake lock failed:', err);
    }

    // Fallback 2: Use periodic touch simulation
    try {
      this.enableTouchSimulation();
      this.startReacquireInterval();
      return true;
    } catch (err) {
      if (DEBUG_WAKELOCK) console.error('[WakeLock] All wake lock methods failed:', err);
      return false;
    }
  }

  /**
   * Enable video-based wake lock
   */
  private async enableVideoWakeLock(): Promise<boolean> {
    if (!this.noSleepVideo) {
      this.noSleepVideo = document.createElement('video');
      this.noSleepVideo.setAttribute('playsinline', '');
      this.noSleepVideo.setAttribute('muted', '');
      this.noSleepVideo.setAttribute('loop', '');
      this.noSleepVideo.style.position = 'fixed';
      this.noSleepVideo.style.top = '-100px';
      this.noSleepVideo.style.left = '-100px';
      this.noSleepVideo.style.width = '1px';
      this.noSleepVideo.style.height = '1px';
      this.noSleepVideo.style.opacity = '0';
      this.noSleepVideo.style.pointerEvents = 'none';
      
      // Use a minimal webm video that loops
      const videoData = 'data:video/webm;base64,GkXfowEAAAAAAAAfQoaBAUL3gQFC8oEEQvOBCEKChHdlYm1Ch4ECQoWBAhhTgGcBAAAAAAACWxFNm3RALE27i1OrhBVJqWZTrIHfTbuMU6uEFlSua1OsggEuTbuMU6uEHFO7a1OsggGw7AEAAAAAAACkAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAVSalmAQAAAAAAAEMq17GDD0JATYCMTGF2ZjU2LjQuMTAxV0GMTGF2ZjU2LjQuMTAxc6SQ20Yj1VAuoQABAAAAAAAAFEiEhq1WAuoQABAAAAAAAABFLSQYjFJAAAAAAAAAAAABEAAAACAQNGCAJicnNgJBgJiciFJBgJkYWpkLuNhAAAAAAAABhUrmsBAAAAAAAA5419fsVLXci0CqbfLzGMV8iIXEtUjRCqXxS8WwNmQ4AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';
      
      this.noSleepVideo.src = videoData;
      document.body.appendChild(this.noSleepVideo);
    }

    // Try to play the video
    try {
      await this.noSleepVideo.play();
      this.isActive = true;
      if (DEBUG_WAKELOCK) console.log('[WakeLock] Fallback video wake lock enabled');
      return true;
    } catch (playErr) {
      if (DEBUG_WAKELOCK) console.warn('[WakeLock] Video play failed:', playErr);
      // Try playing on next user interaction
      this.addPlayOnInteraction();
      return false;
    }
  }

  /**
   * Add play on user interaction for video
   */
  private addPlayOnInteraction(): void {
    const playVideo = async () => {
      if (this.noSleepVideo && this.noSleepVideo.paused) {
        try {
          await this.noSleepVideo.play();
          if (DEBUG_WAKELOCK) console.log('[WakeLock] Video started on user interaction');
          document.removeEventListener('touchstart', playVideo);
          document.removeEventListener('click', playVideo);
        } catch (err) {
          if (DEBUG_WAKELOCK) console.warn('[WakeLock] Video play still failed:', err);
        }
      }
    };

    document.addEventListener('touchstart', playVideo, { once: true });
    document.addEventListener('click', playVideo, { once: true });
  }

  /**
   * Enable touch simulation to prevent sleep
   */
  private enableTouchSimulation(): void {
    // Create invisible button that we'll "click" periodically
    const button = document.createElement('button');
    button.style.position = 'fixed';
    button.style.top = '-9999px';
    button.style.left = '-9999px';
    button.style.width = '1px';
    button.style.height = '1px';
    button.style.opacity = '0';
    button.setAttribute('aria-hidden', 'true');
    document.body.appendChild(button);

    // Simulate interaction every 10 seconds
    const touchInterval = setInterval(() => {
      if (this.isActive) {
        button.click();
        if (DEBUG_WAKELOCK) console.log('[WakeLock] Touch simulation triggered');
      } else {
        clearInterval(touchInterval);
        button.remove();
      }
    }, 10000);

    this.isActive = true;
    if (DEBUG_WAKELOCK) console.log('[WakeLock] Touch simulation wake lock enabled');
  }

  /**
   * Start interval to periodically reacquire wake lock
   */
  private startReacquireInterval(): void {
    // Clear any existing interval
    if (this.reacquireInterval) {
      clearInterval(this.reacquireInterval);
    }

    // Re-acquire wake lock every 30 seconds to combat aggressive power saving
    this.reacquireInterval = setInterval(async () => {
      if (this.isActive && document.visibilityState === 'visible') {
        const timeSinceAcquire = Date.now() - this.lastAcquireTime;
        // Only reacquire if it's been more than 25 seconds
        if (timeSinceAcquire > 25000) {
          if (DEBUG_WAKELOCK) console.log('[WakeLock] Periodic re-acquire triggered');
          await this.requestWakeLock('periodic-reacquire');
        }
      }
    }, 30000);
  }

  /**
   * Release the wake lock
   */
  async releaseWakeLock(): Promise<void> {
    // TEMPORARY: Skip in development
    if (process.env.NODE_ENV === 'development') {
      if (DEBUG_WAKELOCK) console.log('[WakeLock] Release skipped in development');
      return;
    }

    if (DEBUG_WAKELOCK) console.log('[WakeLock] Releasing wake lock');

    // Stop mobile strategies FIRST
    try {
      mobileWakeLock.stop();
    } catch (e) {
      if (DEBUG_WAKELOCK) console.error('[WakeLock] Error stopping mobile strategies:', e);
    }

    // Clear reacquire interval
    if (this.reacquireInterval) {
      clearInterval(this.reacquireInterval);
      this.reacquireInterval = null;
    }

    // Release native wake lock
    if (this.wakeLock) {
      try {
        await this.wakeLock.release();
        this.wakeLock = null;
      } catch (err) {
        if (DEBUG_WAKELOCK) console.warn('[WakeLock] Error releasing wake lock:', err);
      }
    }

    // Stop fallback video
    if (this.noSleepVideo) {
      this.noSleepVideo.pause();
      this.noSleepVideo.remove();
      this.noSleepVideo = null;
    }

    this.isActive = false;
  }

  /**
   * Check if wake lock is currently active
   */
  isWakeLockActive(): boolean {
    return this.isActive;
  }

  /**
   * Re-acquire wake lock after visibility change
   */
  async reacquireWakeLock(): Promise<void> {
    if (this.isActive && document.visibilityState === 'visible') {
      if (DEBUG_WAKELOCK) console.log('[WakeLock] Re-acquiring wake lock after visibility change');
      await this.requestWakeLock('visibility-reacquire');
    }
  }
}

// Create singleton instance
const wakeLockManager = new WakeLockManager();

// Handle visibility changes
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState === 'visible' && wakeLockManager.isWakeLockActive()) {
      // Small delay to ensure the page is fully visible
      setTimeout(() => {
        wakeLockManager.reacquireWakeLock();
      }, 100);
    }
  });

  // Also handle page focus
  window.addEventListener('focus', () => {
    if (wakeLockManager.isWakeLockActive()) {
      if (DEBUG_WAKELOCK) console.log('[WakeLock] Page focused, checking wake lock');
      wakeLockManager.reacquireWakeLock();
    }
  });

  // Reacquire on user interaction if needed
  const interactionHandler = () => {
    if (wakeLockManager.isWakeLockActive()) {
      if (DEBUG_WAKELOCK) console.log('[WakeLock] User interaction detected, checking wake lock');
      wakeLockManager.reacquireWakeLock();
    }
  };

  document.addEventListener('touchstart', interactionHandler, { passive: true });
  document.addEventListener('click', interactionHandler, { passive: true });
}

export default wakeLockManager;

/**
 * React Hook for Wake Lock
 */
export function useWakeLock() {
  const requestWakeLock = async (reason?: string) => {
    return await wakeLockManager.requestWakeLock(reason);
  };

  const releaseWakeLock = async () => {
    return await wakeLockManager.releaseWakeLock();
  };

  const isActive = () => {
    return wakeLockManager.isWakeLockActive();
  };

  return {
    requestWakeLock,
    releaseWakeLock,
    isActive
  };
}
